import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
    listAssets, uploadAsset, deleteAsset, type SocialAsset,
} from '@/services/api/social-publisher';

const KINDS = [
    { value: 'logo', label: 'Logo da marca' },
    { value: 'model_photo', label: 'Foto da pessoa-marca' },
    { value: 'other', label: 'Outro' },
];

export default function AssetsPage() {
    const qc = useQueryClient();
    const { toast } = useToast();
    const [filter, setFilter] = useState<string>('all');
    const [uploadKind, setUploadKind] = useState<string>('logo');
    const fileRef = useRef<HTMLInputElement>(null);

    const { data: assets = [], isLoading } = useQuery({
        queryKey: ['social-publisher', 'assets', filter],
        queryFn: () => listAssets(filter === 'all' ? undefined : { kind: filter }),
    });

    const up = useMutation({
        mutationFn: (file: File) => uploadAsset(file, uploadKind),
        onSuccess: () => {
            toast({ title: 'Upload OK' });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'assets'] });
            if (fileRef.current) fileRef.current.value = '';
        },
        onError: (e: any) => {
            toast({
                title: 'Erro upload',
                description: e?.response?.data?.detail || 'Falha',
                variant: 'destructive',
            });
        },
    });

    const del = useMutation({
        mutationFn: (id: number) => deleteAsset(id),
        onSuccess: () => {
            toast({ title: 'Removido' });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'assets'] });
        },
    });

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to="/meta/social-publisher" className="text-gray-400 hover:text-gray-600">
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div>
                        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <ImageIcon className="w-5 h-5 text-purple-600" /> Mídia
                        </h1>
                        <p className="text-xs text-gray-500">Logos, fotos e imagens geradas</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl border p-5 flex items-end gap-3">
                <div className="flex-1">
                    <Label className="text-xs text-gray-500">Tipo do arquivo</Label>
                    <Select value={uploadKind} onValueChange={setUploadKind}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {KINDS.map((k) => (
                                <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) up.mutate(f);
                    }}
                    className="hidden"
                />
                <Button
                    onClick={() => fileRef.current?.click()}
                    disabled={up.isPending}
                    className="bg-purple-600 hover:bg-purple-700"
                >
                    <Upload className="w-4 h-4 mr-2" />
                    {up.isPending ? 'Enviando...' : 'Upload'}
                </Button>
            </div>

            <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-500">Filtrar:</Label>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-48 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tudo</SelectItem>
                        {KINDS.map((k) => (
                            <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                        ))}
                        <SelectItem value="generated">Geradas (IA)</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {isLoading ? (
                <div className="text-center text-gray-400 py-12">Carregando...</div>
            ) : assets.length === 0 ? (
                <div className="bg-white rounded-xl border p-12 text-center">
                    <ImageIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">Nenhum arquivo</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {assets.map((a: SocialAsset) => (
                        <div key={a.id} className="bg-white rounded-lg border p-2 group relative">
                            <div className="aspect-square bg-gray-50 rounded overflow-hidden flex items-center justify-center">
                                <img
                                    src={a.file_url}
                                    alt={a.file_path}
                                    className="max-w-full max-h-full object-contain"
                                    loading="lazy"
                                />
                            </div>
                            <div className="text-[10px] text-gray-400 mt-1 truncate">
                                #{a.id} · {a.kind}
                            </div>
                            <div className="text-[10px] text-gray-400 truncate">
                                {a.width}×{a.height}
                            </div>
                            <button
                                onClick={() => { if (confirm('Apagar arquivo?')) del.mutate(a.id); }}
                                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-white shadow rounded p-1 text-red-600 hover:bg-red-50"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
